import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl } from "../lib/api";
import { usePromo } from "../context/PromoContext";
import { useCountdown, pad2 } from "../lib/useCountdown";
import { Copy, Check, ArrowRight } from "lucide-react";

const BLOCKED = ["/admin", "/cart", "/checkout", "/order-confirmed", "/login", "/register"];

export const PromoBanner = () => {
  const { campaign } = usePromo();
  const location = useLocation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const c = campaign;
  const cd = useCountdown(c?.end_date);

  if (!c || !c.show_banner) return null;
  if (BLOCKED.some((p) => location.pathname.startsWith(p))) return null;
  if (c.page_scope === "home" && location.pathname !== "/") return null;

  const accent = c.accent_color || "#C5A059";
  const text = c.text_color || "#F4EFE6";
  const bg = c.bg_color || "#0A0A0A";
  const overlay = Math.max(0, Math.min(1, c.overlay_opacity ?? 0.45));

  const goCta = () => {
    const link = (c.cta_link || "").trim();
    if (!link) { navigate("/shop"); return; }
    if (/^https?:\/\//i.test(link)) {
      if (c.cta_new_tab) window.open(link, "_blank", "noopener,noreferrer");
      else window.location.href = link;
    } else {
      navigate(link);
    }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(c.coupon_code || ""); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ }
  };

  const CountBlock = ({ v, l }) => (
    <div className="text-center min-w-[52px]">
      <div className="font-serif-display text-2xl sm:text-3xl leading-none" style={{ color: text }}>{pad2(v)}</div>
      <div className="font-mono-label text-[8px] uppercase tracking-[0.25em] mt-1.5" style={{ color: text, opacity: 0.55 }}>{l}</div>
    </div>
  );

  return (
    <section data-testid="promo-banner" style={{ backgroundColor: bg, color: text }} className="relative w-full overflow-hidden border-b" >
      <div className="absolute inset-x-0 top-0 h-px promo-shimmer" />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 items-stretch">
        {/* Artwork */}
        <div className="relative min-h-[220px] sm:min-h-[300px] lg:min-h-[420px] overflow-hidden">
          <motion.img
            initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            src={fileUrl(c.desktop_image || c.mobile_image)} alt={c.title || "Campaign"}
            loading="lazy" decoding="async"
            className="hidden sm:block absolute inset-0 w-full h-full object-cover" draggable={false}
          />
          <img
            src={fileUrl(c.mobile_image || c.desktop_image)} alt={c.title || "Campaign"}
            loading="lazy" decoding="async"
            className="sm:hidden absolute inset-0 w-full h-full object-cover" draggable={false}
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${bg} 0%, ${hexA(bg, 0)} 60%)`, opacity: 0.35 }} />
          <div className="absolute inset-0" style={{ backgroundColor: bg, opacity: overlay }} />
          <div className="absolute inset-0 lg:hidden" style={{ background: `linear-gradient(0deg, ${bg} 0%, ${hexA(bg, 0)} 55%)` }} />
        </div>

        {/* Content */}
        <motion.div
          initial="hidden" animate="show"
          variants={{ show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } } }}
          className="flex flex-col justify-center gap-4 sm:gap-5 px-6 sm:px-10 py-9 sm:py-12 lg:py-14 -mt-16 sm:mt-0 relative"
        >
          <motion.p variants={fadeUp} className="font-mono-label text-[10px] sm:text-[11px] uppercase tracking-[0.35em]" style={{ color: accent }}>
            {c.eyebrow || "Limited Time Only"}
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]" style={{ color: text }}>
            {c.title}
          </motion.h2>
          {c.subtitle && (
            <motion.p variants={fadeUp} className="text-sm sm:text-base max-w-md leading-relaxed" style={{ color: text, opacity: 0.72 }}>
              {c.subtitle}
            </motion.p>
          )}

          {c.coupon_code ? (
            <motion.div variants={fadeUp} className="flex items-center gap-4 flex-wrap">
              <div className="flex items-stretch">
                <span className="font-mono-label text-[9px] uppercase tracking-[0.25em] flex items-center px-3 border border-r-0" style={{ borderColor: hexA(accent, 0.4), color: text, opacity: 0.7 }}>
                  {c.coupon_label || "Use Code"}
                </span>
                <button data-testid="promo-banner-code" onClick={copyCode} title="Copy code"
                  className="group inline-flex items-center gap-2 px-4 py-2.5 border transition-colors" style={{ borderColor: hexA(accent, 0.55), color: accent }}>
                  <span className="font-mono-label text-sm tracking-[0.2em] font-semibold">{c.coupon_code}</span>
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />}
                </button>
              </div>
            </motion.div>
          ) : null}

          {!cd.expired && c.end_date && (
            <motion.div variants={fadeUp} data-testid="promo-banner-countdown" className="flex flex-col gap-2">
              <span className="font-mono-label text-[9px] uppercase tracking-[0.3em]" style={{ color: text, opacity: 0.5 }}>Offer Ends In</span>
              <div className="flex items-center gap-3 sm:gap-4">
                <CountBlock v={cd.days} l="Days" />
                <span className="font-serif-display text-2xl -mt-3" style={{ color: accent }}>:</span>
                <CountBlock v={cd.hours} l="Hours" />
                <span className="font-serif-display text-2xl -mt-3" style={{ color: accent }}>:</span>
                <CountBlock v={cd.minutes} l="Minutes" />
              </div>
            </motion.div>
          )}

          <motion.div variants={fadeUp} className="pt-1">
            <button data-testid="promo-banner-cta" onClick={goCta}
              className="inline-flex items-center gap-2 px-8 py-3.5 font-mono-label text-xs uppercase tracking-[0.25em] transition-transform duration-300 hover:-translate-y-0.5"
              style={{ backgroundColor: accent, color: bg }}>
              {c.cta_text || "Shop Now"} <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };

// Build an rgba() from a #hex with a given alpha (for gradients/borders).
function hexA(hex, a) {
  const h = (hex || "#000000").replace("#", "");
  const n = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const r = parseInt(n.slice(0, 2), 16) || 0;
  const g = parseInt(n.slice(2, 4), 16) || 0;
  const b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export default PromoBanner;
